import * as React from 'react';
import { t, tryT, type MessageKey } from '@reos/i18n';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { IntakeHeader, useOnline } from '../components/field-intake/IntakeHeader.js';
import { StartStep } from '../components/field-intake/StartStep.js';
import { RecordingStep } from '../components/field-intake/RecordingStep.js';
import { ReviewStep, type CaseOption } from '../components/field-intake/ReviewStep.js';
import { IntakeChecklist } from '../components/field-intake/IntakeChecklist.js';
import { IntakeRooms } from '../components/field-intake/IntakeRooms.js';
import {
  analyze,
  type DictationSuggestionDto,
  getChecklistTemplate,
  getIntakeContext,
  getRoomTemplate,
  getSessionView,
  transcribe,
  type ChecklistTemplateDto,
  type ChecklistTemplateKind,
  type IntakeContextDto,
  type IntakeSessionViewDto,
  type RoomTemplateDto,
} from '../lib/field-intake-api.js';
import { intakeQueue, type OfflineQueue, type QueueRejection } from '../lib/field-intake-queue.js';
import { checklistWriteValue } from '../lib/intake-checklist.js';
import { browserRecorder, type Recorder } from '../lib/field-intake-recorder.js';
import {
  checklistScope,
  intakeChecklistStore,
  type ChecklistMark,
  type ChecklistState,
  type ChecklistStore,
} from '../lib/field-intake-checklist.js';
import { roomListFactValue, setRoomField, ROOM_LIST_FACT_KEY, type Room } from '../lib/intake-rooms.js';
import {
  dictationStateOf,
  groupDictationBody,
  roomDictationBody,
  withoutSuggestion,
  type DictationStates,
} from '../lib/intake-dictation.js';
import { listCases } from '../lib/cases-api.js';
import '../styles/nabor.css';

/**
 * `/nabor` (krok UXV4c) — NÁBOR NA MÍSTĚ: makléř mluví, REOS zapisuje.
 *
 * Vstup z „Nová" (bez zakazky, adresa z formulare) nebo z udalosti
 * kalendare (`?caseId=&eventId=&address=`). Faze:
 *   start (souhlas) -> nahravani (offline fronta) -> zpracovani (odeslani,
 *   prepis, rozbor) -> rozbor (potvrzeni do zakazky).
 * Obrazovka je v rezimu soustredeni (bez menu), jako predloha `Nabor.png`.
 *
 * Krok NAB2 — pri nahravani ma obrazovka DVE ZALOZKY: „Nahrávání" a
 * „Checklist" (zaskrtavaci formular podle sablony `byt-prodej`). Obe zapisuji
 * TOUTEZ cestou (`markPoint`), takze odpoved z checklistu dojde do zakazky
 * stejne jako odskrtnuty bod nebo udaj z nahravky.
 *
 * Krok NAB1 — obrazovka drzi RUCNI CHECKLIST bodu naboru: stav prezije
 * obnoveni stranky (`field-intake-checklist.ts`) a hodnota odskrtnuteho bodu
 * jde offline frontou do relace, takze se do zakazky propise stejnou cestou
 * jako udaj z nahravky. Rozvrzeni desktopu (>= 1024 px) je v `nabor.css`.
 */

type Phase = 'start' | 'recording' | 'processing' | 'review';
/** Krok NAB2 — zalozky faze nahravani; NAB3 pridal „Místnosti". */
type RecordingTab = 'recording' | 'checklist' | 'rooms';

export interface NaborPageProps {
  /** Pro testy: atrapa nahravani a fronty (vychozi = prohlizec + IndexedDB). */
  readonly recorder?: Recorder | undefined;
  readonly queue?: OfflineQueue | undefined;
  readonly newId?: (() => string) | undefined;
  /** Krok NAB1 — uloziste rucne odskrtnutych bodu (vychozi = IndexedDB). */
  readonly checklistStore?: ChecklistStore | undefined;
}

/**
 * UUID pro offline zaznamy. `crypto.randomUUID` je jen v zabezpecenem kontextu
 * (HTTPS); `getRandomValues` je vsude — stejny tvar UUID v4.
 */
export function clientUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Krok P8F2 — JMENO POLE v hlasce o odmitnutem udaji. Popisek je tentyz
 * katalogovy klic jako v detailu nemovitosti (`property.fact.*`); kdyz ho
 * katalog nezna, stoji tam alespon klic — hlaska o vade nesmi shodit
 * obrazovku, na ktere makler tu vadu opravuje (poučeni S17i).
 */
function rejectionFieldLabel(factKey: string | null): string {
  if (factKey === null) return t('webAdmin.fieldIntake.queue.rejectedOther', { locale: 'cs' });
  return tryT(`property.fact.${factKey}` as MessageKey, { locale: 'cs' }) ?? factKey;
}

function readSearch(search: Record<string, unknown>): { caseId: string | null; eventId: string | null; address: string } {
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
  return { caseId: str(search['caseId']), eventId: str(search['eventId']), address: str(search['address']) ?? '' };
}

export function NaborPage({
  recorder = browserRecorder,
  queue: injectedQueue,
  newId = clientUuid,
  checklistStore: injectedChecklist,
}: NaborPageProps): React.JSX.Element {
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const { caseId, eventId, address: initialAddress } = React.useMemo(() => readSearch(search), [search]);
  const queue = React.useMemo(() => injectedQueue ?? intakeQueue(), [injectedQueue]);
  const checklist = React.useMemo(() => injectedChecklist ?? intakeChecklistStore(), [injectedChecklist]);
  const online = useOnline();

  const [phase, setPhase] = React.useState<Phase>('start');
  const [context, setContext] = React.useState<IntakeContextDto | null>(null);
  const [address, setAddress] = React.useState(initialAddress);
  const [consent, setConsent] = React.useState(false);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(0);
  /** Krok P8F2 — polozky, ktere server odmitl (422): cekaji na opravu maklerem. */
  const [rejections, setRejections] = React.useState<readonly QueueRejection[]>([]);
  const [view, setView] = React.useState<IntakeSessionViewDto | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [cases, setCases] = React.useState<readonly CaseOption[]>([]);
  const [marks, setMarks] = React.useState<ChecklistState>({});
  const [tab, setTab] = React.useState<RecordingTab>('recording');
  const [template, setTemplate] = React.useState<ChecklistTemplateDto | null>(null);
  /**
   * Krok NAB6 — RUCNE PREPNUTA SABLONA. `null` = jede predvolba podle
   * zakazky (`context.checklistKind`); jakmile makler prepne, drzi se jeho
   * volba a checklist k ni ukaze varovani.
   */
  const [manualKind, setManualKind] = React.useState<ChecklistTemplateKind | null>(null);
  /** Krok NAB3 — sablona mistnosti (typy, rozpad dispozice, predvolby). */
  const [roomTemplate, setRoomTemplate] = React.useState<RoomTemplateDto | null>(null);
  /** Pocet fotek u mistnosti — jen stav obrazovky (fotky jdou frontou). */
  const [roomPhotos, setRoomPhotos] = React.useState<Readonly<Record<string, number>>>({});
  /** Krok NAB4 — stav diktatu u kazdeho mikrofonu (`group:<id>` / `room:<id>`). */
  const [dictations, setDictations] = React.useState<DictationStates>({});
  /*
   * Posledni znamy stav i mezi dvema vykreslenimi: odskrtnuti bodu a zapsani
   * jeho hodnoty jsou dve akce hned za sebou a druha z nich by jinak vysla ze
   * zastaraleho `marks` a tu prvni zahodila.
   */
  const marksRef = React.useRef<ChecklistState>(marks);

  /*
   * Krok NAB1 — odskrtnute body patri NEMOVITOSTI (zakazka nebo adresa), ne
   * relaci: po obnoveni stranky zacina nova relace, ale nabor je tentyz.
   */
  const scope = React.useMemo(() => checklistScope(caseId, address), [caseId, address]);
  React.useEffect(() => {
    let cancelled = false;
    void checklist.load(scope).then((stored) => {
      if (cancelled) return;
      marksRef.current = stored;
      setMarks(stored);
    });
    return () => {
      cancelled = true;
    };
  }, [checklist, scope]);

  /*
   * Krok KLIKFIX1 (vada V8) — NAVRAT DO ROZPRACOVANEHO NABORU.
   *
   * Po obnoveni stranky zacinal nabor vzdy na uvodni obrazovce a „Diktovat si
   * sám" zalozilo NOVOU relaci; fotky i udaje predchozi relace zustaly ve
   * fronte bez cesty k nim (klikaci test 29. 10., snimek
   * `d-95-nabor-po-reloadu.png`). Id relace proto lezi u teze nemovitosti
   * jako odskrtnute body a obrazovka se do nahravani vrati sama.
   *
   * Jen kdyz makler jeste nezacal (`phase === 'start'`): rozehrany nabor
   * v teto zalozce se nesmi prepnout pod rukama.
   */
  React.useEffect(() => {
    let cancelled = false;
    void checklist.loadSession(scope).then((stored) => {
      if (cancelled || stored === null) return;
      setSessionId((prev) => prev ?? stored);
      setPhase((prev) => (prev === 'start' ? 'recording' : prev));
    });
    return () => {
      cancelled = true;
    };
  }, [checklist, scope]);

  /*
   * Krok NAB6 — KTERA SABLONA. Predvolbu rika server z druhu zakazky
   * a nemovitosti (`checklistKind`); rucni volba maklere ji prebiji. Nez
   * kontext zakazky dojede, nestahuje se nic — jinak by makler na chvili
   * videl checklist bytu na prodej i u naboru najmu.
   */
  const expectedKind = context?.checklistKind ?? null;
  const templateKind: ChecklistTemplateKind | null =
    manualKind ?? expectedKind ?? (caseId === null || context !== null ? 'byt-prodej' : null);

  /*
   * Sablona checklistu je ciselnik kancelare, ne data zakazky — stahuje se
   * jednou pro dane jmeno sablony. Kdyz nedojede (offline), zalozka to rekne
   * a nahravani funguje dal.
   */
  React.useEffect(() => {
    if (templateKind === null) return undefined;
    let cancelled = false;
    getChecklistTemplate(templateKind)
      .then((loaded) => {
        if (!cancelled) setTemplate(loaded);
      })
      .catch(() => {
        // Offline: checklist se ukaze, az bude sit.
      });
    return () => {
      cancelled = true;
    };
  }, [templateKind]);

  React.useEffect(() => {
    let cancelled = false;
    getRoomTemplate()
      .then((loaded) => {
        if (!cancelled) setRoomTemplate(loaded);
      })
      .catch(() => {
        // Offline: zalozka mistnosti se ukaze, az bude sit (stejne jako checklist).
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Krok NAB4 — navrhy z rozboru diktatu. Chodi pres frontu, protoze bez
   * signálu se diktat jen ulozi a odesle az po pripojeni; obrazovka je pak
   * ukaze u bodu dane skupiny nebo mistnosti.
   */
  React.useEffect(
    () =>
      queue.onDictation((scopeKey, result) => {
        setDictations((prev) => ({ ...prev, [scopeKey]: dictationStateOf(result) }));
      }),
    [queue],
  );

  React.useEffect(() => queue.onChange(setPending), [queue]);
  React.useEffect(() => queue.attachOnlineListener(window), [queue]);
  React.useEffect(() => {
    void queue.pending().then(setPending);
    /*
     * Krok P8F2 — odlozena polozka prezije obnoveni stranky (lezi
     * v IndexedDB), takze se musi precist uz pri nabehu obrazovky; jinak by
     * makler videl frontu „0" a nevedel, proc nabor nejde potvrdit.
     */
    void queue.rejections().then(setRejections);
  }, [queue]);

  React.useEffect(() => {
    let cancelled = false;
    getIntakeContext(caseId)
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
        if (initialAddress === '' && ctx.case?.address) setAddress(ctx.case.address);
      })
      .catch(() => {
        // Offline nebo bez zakazky: nabor jde i bez „REOS už zná".
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, initialAddress]);

  const title = context?.case?.title ?? (address !== '' ? address : t('webAdmin.fieldIntake.title', { locale: 'cs' }));

  const start = async (): Promise<void> => {
    const id = newId();
    setSessionId(id);
    setPhase('recording');
    await queue.enqueue({
      kind: 'session',
      sessionId: id,
      body: { caseId, calendarEventId: eventId, addressText: address, mode: consent ? 'conversation' : 'self_dictation' },
    });
    if (consent) await queue.enqueue({ kind: 'consent', sessionId: id });
    /*
     * Krok KLIKFIX1 (vada V8): relace prezije obnoveni stranky. Zapis stoji
     * ZA frontou zamerne — souhlas musi odejit pred nahravkou rozhovoru
     * (spec „souhlas odejde pred nahravkou"), takze se mezi ne nevklinuje nic.
     */
    await checklist.saveSession(scope, id);
  };

  const process = React.useCallback(async (): Promise<void> => {
    if (sessionId === null) return;
    setProblem(null);
    const flushed = await queue.flush();
    setRejections(flushed.rejections);
    if (flushed.rejected !== null) {
      setProblem(flushed.rejected.message);
      return;
    }
    /*
     * Krok P8F2 — potvrzeni naboru CEKA, dokud makler odmitnutou polozku
     * neopravi nebo nesmaze: rozbor by jinak jel nad relaci, ktere ten udaj
     * chybi, a makler by se to dozvedel teprve u prazdneho bodu v zakazce.
     */
    if (flushed.rejections.length > 0) return;
    if (flushed.pending > 0) return; // ceka na sit — `online` listener fronty to zkusi znovu
    try {
      let current = await getSessionView(sessionId);
      if (current.session.transcriptStatus === 'none' || current.session.transcriptStatus === 'failed') {
        await transcribe(sessionId);
        current = await getSessionView(sessionId);
      }
      /*
       * Krok NAB1: o rozbor se zada, kdyz jeste NENI zadny navrh Z NAHRAVKY.
       * Do NAB1 stalo `items.length === 0`, takze jedina rucne odskrtnuta
       * hodnota (ta je taky polozka relace) rozbor nahravky uplne zrusila.
       */
      if (
        current.session.transcriptStatus === 'done' &&
        current.extractionAvailable &&
        !current.items.some((item) => item.origin === 'ai')
      ) {
        await analyze(sessionId);
        current = await getSessionView(sessionId);
      }
      if (current.session.caseId === null) {
        const list = await listCases().catch(() => ({ items: [] as { id: string; title: string }[] }));
        setCases(list.items.map((c) => ({ id: c.id, title: c.title })));
      }
      setView(current);
      setPhase('review');
    } catch (e) {
      setProblem(e instanceof Error ? e.message : t('webAdmin.fieldIntake.processing.failed', { locale: 'cs' }));
    }
  }, [queue, sessionId]);

  // Po obnoveni site pokracuje zpracovani samo.
  React.useEffect(() => {
    if (phase === 'processing' && online && pending === 0 && view === null) void process();
  }, [phase, online, pending, view, process]);

  const reload = async (): Promise<void> => {
    if (sessionId !== null) setView(await getSessionView(sessionId));
  };

  /*
   * Krok NAB1 — ODSKRTNUTI BODU. Stav obrazovky jde do uloziste (prezije
   * obnoveni stranky i bez site) a HODNOTA navic do relace naboru offline
   * frontou — odtud se v rozboru objevi jako navrh s puvodem „ručně" a do
   * zakazky se zapise stejnou cestou jako udaj z nahravky. Odskrtnuti BEZ
   * hodnoty se do relace neposila: relace zna jen fakta s hodnotou, „zjisteno,
   * hodnotu nezapisujeme" je informace pro maklere na teto obrazovce.
   */
  const persistMarks = (next: ChecklistState): void => {
    marksRef.current = next;
    setMarks(next);
    void checklist.save(scope, next);
  };

  const markPoint = (factKey: string, value: unknown, atSeconds = 0, note?: string): void => {
    const mark: ChecklistMark = { factKey, source: 'manual', ...(value === undefined ? {} : { value }) };
    persistMarks({ ...marksRef.current, [factKey]: mark });
    if (value === undefined || sessionId === null) return;
    /*
     * Krok P8F2 — HODNOTA PRO ZAPIS. Na obrazovce drzi multiselect POLE
     * hodnot (jinak by se druhe klepnuti chovalo jako prepnuti), ale do faktu
     * typu `text` (`heating_type`, `parking_type`) musi jit jeden text —
     * jinak ho server odmitne (422). Prevod je TADY, protoze tohle je jedine
     * misto, kterym odpoved checklistu, mistnosti i potvrzeny navrh diktatu
     * odchazi do relace naboru.
     */
    const point = template?.points.find((candidate) => candidate.factKey === factKey);
    const written = point === undefined ? value : checklistWriteValue(point, value);
    void queue
      .enqueue({ kind: 'manualFact', sessionId, factKey, value: written, atSeconds, ...(note === undefined ? {} : { note }) })
      .then((flushed) => {
        setRejections(flushed.rejections);
        if (flushed.rejected !== null) {
          setProblem(flushed.rejected.message);
          return;
        }
        // V rozboru uz seznam navrhu na obrazovce je — musi se znovu nacist.
        if (phase === 'review' && flushed.pending === 0) void reload();
      });
  };

  const unmarkPoint = (factKey: string): void => {
    const next = { ...marksRef.current };
    delete next[factKey];
    persistMarks(next);
  };

  /*
   * Krok NAB3 — mistnosti a dispozice ze stavu obrazovky. Dispozice je
   * odpoved checklistu na bod `property_layout` (fakt `disposition`); seznam
   * mistnosti je hodnota faktu `room_list`.
   */
  const rooms = React.useMemo<readonly Room[]>(() => {
    const value = marks[ROOM_LIST_FACT_KEY]?.value;
    return Array.isArray(value) ? (value as readonly Room[]) : [];
  }, [marks]);
  const dispositionAnswer = typeof marks['disposition']?.value === 'string' ? (marks['disposition'].value as string) : null;
  const roomsNote = t('webAdmin.fieldIntake.checklistTab.origin.observed', { locale: 'cs' });

  /*
   * Krok NAB4 — DIKTAT KE SKUPINE NEBO MISTNOSTI. Nahravka jde do fronty jako
   * kazda jina (tedy i bez signálu) a hned za ni pozadavek na rozbor s
   * OMEZENIM na body te skupiny nebo pole mistnosti. Vysledek jsou navrhy;
   * do zakazky se nic nezapise, dokud makler neklepne na „Potvrdit".
   */
  const dictate = (scopeKey: string, blob: Blob, durationS: number): void => {
    if (sessionId === null) return;
    const recordingId = newId();
    setDictations((prev) => ({ ...prev, [scopeKey]: { status: 'pending', text: '', suggestions: [] } }));
    const roomId = scopeKey.startsWith('room:') ? scopeKey.slice('room:'.length) : null;
    const body =
      roomId === null
        ? groupDictationBody(recordingId, scopeKey.slice('group:'.length))
        : roomDictationBody(recordingId, roomId, rooms);
    void queue
      .enqueue({
        kind: 'recording',
        sessionId,
        id: recordingId,
        blob,
        recordingKind: 'self_dictation',
        durationS,
      })
      .then(() => queue.enqueue({ kind: 'dictation', sessionId, scopeKey, body }))
      .then((flushed) => {
        setRejections(flushed.rejections);
        if (flushed.rejected !== null) {
          setProblem(flushed.rejected.message);
          setDictations((prev) => ({ ...prev, [scopeKey]: { status: 'unavailable', text: '', suggestions: [] } }));
        }
      });
  };

  /**
   * Potvrzeni navrhu JEDNIM klepnutim. Zapis jde TOUTEZ cestou jako kazda
   * odpoved checklistu (`markPoint` -> fronta -> polozka relace -> potvrzeni),
   * takze druha cesta zapisu nevznika. Potvrzeny navrh prestane svitit.
   */
  const acceptSuggestion = (suggestion: DictationSuggestionDto): void => {
    if (suggestion.kind === 'point') {
      const point = template?.points.find((candidate) => candidate.id === suggestion.pointId);
      if (point?.factKey == null) return;
      const origin = point.defaultOrigin;
      markPoint(
        point.factKey,
        suggestion.value,
        suggestion.atSeconds,
        t(`webAdmin.fieldIntake.checklistTab.origin.${origin}` as Parameters<typeof t>[0], { locale: 'cs' }),
      );
    } else if (roomTemplate !== null && suggestion.roomId !== null && suggestion.fieldId !== null) {
      const current = marksRef.current[ROOM_LIST_FACT_KEY]?.value;
      const list = Array.isArray(current) ? (current as readonly Room[]) : rooms;
      const next = setRoomField(
        roomTemplate,
        list,
        suggestion.roomId,
        suggestion.fieldId,
        suggestion.value as Room[string],
      );
      markPoint(ROOM_LIST_FACT_KEY, roomListFactValue(roomTemplate, next), suggestion.atSeconds, roomsNote);
    }
    setDictations((prev) => withoutSuggestion(prev, suggestion));
  };

  const dismissSuggestion = (suggestion: DictationSuggestionDto): void => {
    setDictations((prev) => withoutSuggestion(prev, suggestion));
  };

  const close = (): void => {
    void navigate({ to: caseId !== null ? '/zakazky/$id' : '/prehled', ...(caseId !== null ? { params: { id: caseId } } : {}) } as never);
  };

  return (
    <div className="rh-fi-page" data-testid="nabor-page" data-guide="intake" data-phase={phase}>
      <IntakeHeader
        title={phase === 'review' ? t('webAdmin.fieldIntake.review.navTitle', { locale: 'cs' }) : t('webAdmin.fieldIntake.title', { locale: 'cs' })}
        back={
          phase === 'review'
            ? { label: t('webAdmin.fieldIntake.review.back', { locale: 'cs' }), onClick: close }
            : { label: t('webAdmin.fieldIntake.close', { locale: 'cs' }), onClick: close }
        }
        backIsChevron={phase === 'review'}
        online={online}
        pending={pending}
      />

      {/*
        Krok P8F2 — ODMITNUTY UDAJ. Server rekl, ze hodnotu prijmout nemuze
        (422); opakovani ji neprotlaci, takze tu stoji JMENEM POLE a s duvodem,
        dokud ji makler neopravi (nova odpoved bodu) nebo nesmaze. Zbytek
        fronty uz odesel — fotky ani nahravka na ni necekaji.
      */}
      {rejections.length > 0 ? (
        <section className="rh-v4-group rh-fi-card" data-testid="fi-rejected">
          <div className="rh-fi-card__title">{t('webAdmin.fieldIntake.queue.rejectedTitle', { locale: 'cs' })}</div>
          {rejections.map((rejection) => (
            <p
              key={rejection.seq}
              className="rh-fi-note rh-fi-note--warn"
              role="alert"
              data-testid={`fi-rejected-${rejection.factKey ?? String(rejection.seq)}`}
            >
              {t('webAdmin.fieldIntake.queue.rejectedItem', {
                locale: 'cs',
                values: { field: rejectionFieldLabel(rejection.factKey), reason: rejection.reason },
              })}
            </p>
          ))}
          <div className="rh-fi-card__sub">{t('webAdmin.fieldIntake.queue.rejectedHint', { locale: 'cs' })}</div>
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--plain"
            onClick={() => {
              void Promise.all(rejections.map((rejection) => queue.dropRejection(rejection.seq))).then(() =>
                queue.rejections().then(setRejections),
              );
            }}
            data-testid="fi-rejected-drop"
          >
            {t('webAdmin.fieldIntake.queue.rejectedDrop', { locale: 'cs' })}
          </button>
        </section>
      ) : null}

      {phase === 'start' ? (
        <StartStep
          context={context}
          address={address}
          onAddressChange={setAddress}
          consent={consent}
          onConsentChange={setConsent}
          onStart={() => void start()}
          busy={false}
        />
      ) : null}

      {phase === 'recording' && sessionId !== null ? (
        <div className="rh-fi-tabs" role="tablist" data-testid="nab2-tabs">
          {(['recording', 'checklist', 'rooms'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={`rh-v4-btn rh-v4-btn--chip rh-fi-tab${tab === value ? ' is-on' : ''}`}
              onClick={() => setTab(value)}
              data-testid={`nab2-tab-${value}`}
            >
              {value === 'recording'
                ? t('webAdmin.fieldIntake.checklistTab.tabRecording', { locale: 'cs' })
                : value === 'checklist'
                  ? t('webAdmin.fieldIntake.checklistTab.tabChecklist', { locale: 'cs' })
                  : t('webAdmin.fieldIntake.roomsTab.tabRooms', { locale: 'cs' })}
            </button>
          ))}
        </div>
      ) : null}

      {phase === 'recording' && sessionId !== null && tab === 'checklist' ? (
        <IntakeChecklist
          template={template}
          context={context}
          templateKind={templateKind}
          expectedKind={expectedKind}
          onTemplateKind={setManualKind}
          marks={marks}
          onMark={(factKey, value, note) => markPoint(factKey, value, 0, note)}
          onUnmark={unmarkPoint}
          recorder={recorder}
          dictations={dictations}
          onDictate={dictate}
          onAcceptSuggestion={acceptSuggestion}
          onDismissSuggestion={dismissSuggestion}
        />
      ) : null}

      {/*
        Krok NAB3 — MISTNOSTI. Seznam je JEDEN FAKT (`room_list`) ve stejnem
        stavu obrazovky jako odpovedi checklistu, takze se zapisuje touz cestou
        a prezije obnoveni stranky i bez site. Dispozice se cte z odpovedi
        checklistu — rozpad mistnosti z ni vychazi.
      */}
      {phase === 'recording' && sessionId !== null && tab === 'rooms' ? (
        <IntakeRooms
          template={roomTemplate}
          disposition={dispositionAnswer}
          rooms={rooms}
          onRooms={(next) => {
            if (roomTemplate === null) return;
            markPoint(ROOM_LIST_FACT_KEY, roomListFactValue(roomTemplate, next), 0, roomsNote);
          }}
          onPhoto={(room, file) => {
            const id = newId();
            setRoomPhotos((prev) => ({ ...prev, [room.roomId]: (prev[room.roomId] ?? 0) + 1 }));
            void queue
              .enqueue({
                kind: 'photo',
                sessionId,
                id,
                blob: file,
                atSeconds: 0,
                roomId: room.roomId,
                ...(room.roomType === null ? {} : { roomType: room.roomType }),
              })
              .then((flushed) => {
                setRejections(flushed.rejections);
                if (flushed.rejected !== null) setProblem(flushed.rejected.message);
              });
          }}
          photoCounts={roomPhotos}
          recorder={context?.transcriptionAvailable === true ? recorder : undefined}
          dictations={dictations}
          onDictate={dictate}
          onAcceptSuggestion={acceptSuggestion}
          onDismissSuggestion={dismissSuggestion}
        />
      ) : null}

      {phase === 'recording' && sessionId !== null && tab === 'recording' ? (
        <RecordingStep
          sessionId={sessionId}
          consent={consent}
          context={context}
          title={title}
          recorder={recorder}
          queue={queue}
          newId={newId}
          marks={marks}
          onMark={markPoint}
          onUnmark={unmarkPoint}
          onDone={() => setPhase('processing')}
        />
      ) : null}

      {phase === 'processing' ? (
        <section className="rh-v4-group rh-fi-card" data-testid="fi-processing">
          {/*
            Krok P8F2: „Bez signálu" patri JEN ke skutecnemu vypadku site.
            Do P8F2 stacilo `pending > 0`, takze polozka, kterou server odmitl,
            hlasila makleri se signálem vypadek site (nalez P8-N2).
          */}
          <div className="rh-fi-card__title">
            {!online
              ? t('webAdmin.fieldIntake.processing.offlineTitle', { locale: 'cs' })
              : t('webAdmin.fieldIntake.processing.workingTitle', { locale: 'cs' })}
          </div>
          <div className="rh-fi-card__sub">
            {!online
              ? t('webAdmin.fieldIntake.processing.offlineText', { locale: 'cs', values: { count: pending } })
              : t('webAdmin.fieldIntake.processing.workingText', { locale: 'cs' })}
          </div>
          {problem !== null ? (
            <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid="fi-processing-error">
              {problem}
            </p>
          ) : null}
          <button type="button" className="rh-v4-btn rh-v4-btn--tinted" onClick={() => void process()} data-testid="fi-retry">
            {t('webAdmin.fieldIntake.processing.retry', { locale: 'cs' })}
          </button>
        </section>
      ) : null}

      {phase === 'review' && view !== null ? (
        <ReviewStep
          view={view}
          cases={cases}
          marks={marks}
          onMark={markPoint}
          onUnmark={unmarkPoint}
          onChanged={() => void reload()}
          onOpenCase={(id) => {
            // Krok KLIKFIX1 (vada V8): nabor dojel do zakazky, takze uz
            // neni co obnovovat — jinak by se obrazovka vracela do uzavrene
            // relace i pri dalsim naboru teze nemovitosti.
            void checklist.saveSession(scope, null);
            void navigate({ to: '/zakazky/$id', params: { id } });
          }}
          onOpenListing={(id) => {
            void checklist.saveSession(scope, null);
            void navigate({ to: '/zakazky/$id/inzerat', params: { id } });
          }}
        />
      ) : null}
    </div>
  );
}
